'use client';
import { BazaarVoiceWidgetSx } from '@/components/content/HdmBazaarrvoiceRQ/styles/style';
import {
	NAVIGABLE_SECTION_IDS,
	useNavigableAccordionSection,
} from '@/components/content/ProductDetails/hooks/useNavigableAccordionSection';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { WidgetComponentProps } from '@/data/types/Slot';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Stack,
	Typography,
} from '@mui/material';
import { FC } from 'react';

type WidgetProperties = {
	bazaarvoiceType?: string;
};

const emptyProperties = {} as WidgetProperties;

const REVIEWS_KEY = 'reviews';
const QA_KEY = 'questions_answers';

export const HdmBazaarrvoiceRQ: FC<WidgetComponentProps> = ({
	id,
	properties = emptyProperties,
}) => {
	const widgetType = properties?.bazaarvoiceType;
	const isQA = widgetType === QA_KEY;

	const navigableSectionProps = useNavigableAccordionSection({
		sectionId: isQA
			? NAVIGABLE_SECTION_IDS.PRODUCT_QUESTIONS
			: NAVIGABLE_SECTION_IDS.PRODUCT_REVIEWS,
	});
	const isExpanded = navigableSectionProps.expanded;

	const labels = useLocalization('BazaarVoice');
	const {
		settings: { userData },
	} = useSettings();

	
	const { isBuyable } = useProductDetails({
		partNumber: id.toString(),
	});
	const isBVReviewsEnabled = userData?.ENABLE_BAZARVOICE_REVIEWS === 'true' ? true : false;
	const isBVQuestionsEnabled = userData?.ENABLE_BAZARVOICE_QUESTIONS === 'true' ? true : false;
	const productPartNumber = id;

	const widget = {
		title: isQA ? labels.QuestionsAnswers.t() : labels.Reviews.t(),
		linkText: isQA ? labels.AskAQuestion.t() : labels.ReviewThisProduct.t(),
		componentName: isQA ? 'questions' : REVIEWS_KEY,
		show: isQA ? isBVQuestionsEnabled : isBVReviewsEnabled,
		cardId: isQA ? 'customer-questions-answers-card' : 'customer-reviews-card',
	};

	if (!widget.show || !isBuyable) {
		return null;
	}

	return (
		<Accordion
			id={widget.cardId}
			sx={{
				width: '100%',
			}}
			disableGutters
			{...navigableSectionProps}
		>
			<AccordionSummary
				expandIcon={
					isExpanded ? (
						<RemoveIcon id={`toggle${isQA ? 'QA' : 'Reviews'}Card`} />
					) : (
						<AddIcon id={`toggle${isQA ? 'QA' : 'Reviews'}Card`} />
					)
				}
				sx={BazaarVoiceWidgetSx.accordionSummary}
			>
				<Stack sx={BazaarVoiceWidgetSx.accordionHeader}>
					<Stack direction="row" alignItems="baseline" spacing={1.25}>
						<Typography
							variant="headingSegment"
							component="h3"
							sx={BazaarVoiceWidgetSx.accordionTitle}
						>
							{widget.title}
						</Typography>
						{!isExpanded ? (
							<Stack
								onClick={(e) => e.stopPropagation()}
								sx={
									widgetType === REVIEWS_KEY
										? BazaarVoiceWidgetSx.ratingSummaryContent
										: BazaarVoiceWidgetSx.askAQuestionContent
								}
							>
								<div
									id={`create${isQA ? 'Question' : 'Review'}Shortcut`}
									data-bv-show="rating_summary"
									data-bv-product-id={productPartNumber}
								/>
							</Stack>
						) : null}
					</Stack>
				</Stack>
			</AccordionSummary>
			<AccordionDetails sx={BazaarVoiceWidgetSx.accordionDetails}>
				<Box
					id={`${isQA ? 'questionsAnswers' : 'reviews'}Card`}
					sx={BazaarVoiceWidgetSx.accordionContainer}
				>
					<div data-bv-show={widget.componentName} data-bv-product-id={productPartNumber} />
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};
